import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn, TableAction } from "@shared/types/table";
import { PAGE_SIZE } from "@shared/utils/constants";
import { useToast } from "@shared/hooks/use-toast";
import { Button } from "@shared/ui/button";
import { initiateDownloadZip } from "../../api";
import { usePrintingExport, usePrintList, useVendorList } from "../../hooks";
import { PrintingFilterPanel } from "./PrintingFilter";
import { StatusBadge } from "./StatusBadge";
import { logEvent } from "@shared/analytics/analytics";
import { CircleCheckBig, Download, Printer, SquarePen, Info, Eye } from "lucide-react";
import AcceptBillCycleModal from "./acceptBillCycleModal";
import EditPrintingStatusModal from "./editPrintingStatusModal";
import ViewPrintingDetailsModal from "./ViewPrintingDetailsModal";

interface CycleData {
  id: string;
  start_date: string;
  end_date: string;
  total_bill_count: number;
  cycle_name: string;
  cycle_id: number;
  run_id: number;
  printing_date: string;
  completion_date: string | null;
  vendor: any;
  lapsed_days: number;
  printing_status: string;
  file: string;
}

interface PrintingFilters {
  status: string;
  vendor: string;
  startDate: string;
  endDate: string;
}

export function PrintingTable() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();

  // Read URL params
  const searchQuery = searchParams.get("query") || "";
  const statusFilter = searchParams.get("status") || "";
  const vendorFilter = searchParams.get("vendor") || "";
  const startDateParam = searchParams.get("startDate") || "";
  const endDateParam = searchParams.get("endDate") || "";
  const currentPage = parseInt(searchParams.get("page") || "1");

  // Build API filters
  const apiFilters = {
    page: currentPage,
    limit: PAGE_SIZE,
    ...(statusFilter &&
      statusFilter !== "" && { printing_status: statusFilter }),
    ...(vendorFilter && vendorFilter !== "" && { vendor: vendorFilter }),
    ...(startDateParam && { start_date: startDateParam }),
    ...(endDateParam && { end_date: endDateParam }),
    ...(searchQuery && { search_data: searchQuery }),
  };

  const { data: printingData } = usePrintList(apiFilters);

  // Export state
  const exportFilters = { ...apiFilters };
  delete exportFilters.page;
  delete exportFilters.limit;
  const { exportCSV, isExporting } = usePrintingExport(exportFilters);

  // Modal states
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isVendorEditModalOpen, setIsVendorEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedCycle, setSelectedCycle] = useState<any | null>(null);

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: PAGE_SIZE,
    startItem: 0,
    endItem: 0,
  });

  // Update pagination based on API data
  useEffect(() => {
    const totalItems = printingData?.count || 0;
    const totalPages = Math.ceil(totalItems / PAGE_SIZE);
    const startItem = (currentPage - 1) * PAGE_SIZE + 1;
    const endItem = Math.min(currentPage * PAGE_SIZE, totalItems);

    setPagination({
      currentPage: currentPage,
      totalPages,
      totalItems,
      itemsPerPage: PAGE_SIZE,
      startItem: totalItems > 0 ? startItem : 0,
      endItem: totalItems > 0 ? endItem : 0,
    });
  }, [printingData?.count, currentPage]);

  // Handle search
  const handleSearch = (searchTerm: string) => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
    };

    if (searchTerm.trim()) {
      newParams.query = searchTerm.trim();
    } else {
      delete newParams.query;
    }

    // Reset to page 1 on new search
    delete newParams.page;

    setSearchParams(newParams);
  };

  // Apply filters
  const handleApplyFilters = (filters: PrintingFilters) => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
    };

    if (filters.status && filters.status !== "") {
      newParams.status = filters.status;
    } else {
      delete newParams.status;
    }

    if (filters.vendor && filters.vendor !== "") {
      newParams.vendor = filters.vendor;
    } else {
      delete newParams.vendor;
    }

    if (filters.startDate) {
      newParams.startDate = filters.startDate;
    } else {
      delete newParams.startDate;
    }

    if (filters.endDate) {
      newParams.endDate = filters.endDate;
    } else {
      delete newParams.endDate;
    }

    // Reset to page 1 on filter change
    delete newParams.page;

    setSearchParams(newParams);
  };

  // Reset filters
  const handleResetFilters = () => {
    const newParams: Record<string, string> = {};
    if (searchQuery) {
      newParams.query = searchQuery;
    }
    setSearchParams(newParams);
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
      page: page.toString(),
    };
    setSearchParams(newParams);
  };

  // Handle export
  const handleExportCSV = () => {
    exportCSV();
    logEvent("BX Printing Cycles Exported");
  };

  // Modal handlers
  const openEditModal = (cycle: CycleData) => {
    setSelectedCycle(cycle);
    setIsEditModalOpen(true);
  };

  const openViewModal = (cycle: CycleData) => {
    setSelectedCycle(cycle);
    setIsViewModalOpen(true);
  };

  const openVendorModal = (cycle: CycleData) => {
    setSelectedCycle(cycle);
    setIsVendorEditModalOpen(true);
  };

  const handlePrint = (fileUrl: string) => {
    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = "";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownload = (cycleId: number) => {
    initiateDownloadZip(cycleId);
    toast({
      title: "Download In Progress...",
      description: "Please refresh after 5 minutes to download the zip",
    });
  };

  // Check if any filters are active
  const hasActiveFilters =
    (statusFilter && statusFilter !== "") ||
    (vendorFilter && vendorFilter !== "") ||
    startDateParam ||
    endDateParam;

  // Count active filters
  const activeFilterCount = [
    !!(statusFilter && statusFilter !== ""),
    !!(vendorFilter && vendorFilter !== ""),
    !!startDateParam,
    !!endDateParam,
  ].filter(Boolean).length;

  const tableActions: TableAction<any>[] = [
    {
      label: "View Details",
      icon: <Eye className="w-4 h-4" />,
      onClick: (row) => openViewModal(row),
    },
    {
      label: "Edit",
      icon: <SquarePen className="w-4 h-4" />,
      onClick: (row) => openEditModal(row),
    },
    {
      label: "Assign Vendor",
      icon: <CircleCheckBig className="w-4 h-4" />,
      onClick: (row) => openVendorModal(row),
      hidden: (row) => row.printingStatus !== "Pending",
    },
    {
      label: "Download",
      icon: <Download className="w-4 h-4" />,
      onClick: (row) => handleDownload(row.cycleId),
    },
    {
      label: "Print",
      icon: <Printer className="w-4 h-4" />,
      onClick: (row) => handlePrint(row.file),
      hidden: (row) => !row.file || row.file === "",
    },
  ];

  // Define table columns
  const columns: TableColumn<any>[] = [
    {
      key: "cycleName",
      header: "Billing Cycle Name",
      render: (value: string) => <span className="font-medium">{value}</span>,
    },
    {
      key: "billingPeriod",
      header: "Billing Period",
      render: (value: any, row: any) => {
        return `${row.startDate} - ${row.endDate}`;
      },
    },
    {
      key: "totalBillCount",
      header: "Total Bills",
    },
    {
      key: "printingDate",
      header: "Print Date",
    },
    {
      key: "completionDate",
      header: "Completion Date",
    },
    {
      key: "printingStatus",
      header: "Status",
      render: (value: string) => {
        let statusType: any = "pending";
        if (value === "Completed") statusType = "completed";
        else if (value === "In Progress" || value === "Started") statusType = "Started";
        else if (value === "Failed") statusType = "failed";

        return <StatusBadge status={statusType} label={value || "Pending"} />;
      },
    },
    {
      key: "vendor",
      header: "Print By",
      render: (value: any) => (
        <div className="text-center">{value?.vendorName || "NA"}</div>
      ),
    },
    {
      key: "lapsedDays",
      header: "Lapsed Days",
      render: (value: number) => (
        <div className="flex justify-center">
          <StatusBadge
            status={value > 7 ? "warning" : value > 3 ? "pending" : "info"}
            label={`${value ?? "NA"} days`}
          />
        </div>
      ),
    },
  ];

  // Use the filter component
  const filterPanel = (
    <PrintingFilterPanel
      currentFilters={{
        status: statusFilter,
        vendor: vendorFilter,
        startDate: startDateParam,
        endDate: endDateParam,
      }}
      onApplyFilters={handleApplyFilters}
      onResetFilters={handleResetFilters}
    />
  );

  return (
    <div className="space-y-6 mt-6">
      <GenericTable
        data={printingData?.result || []}
        columns={columns}
        rowKey="run_id"
        emptyMessage="No printing cycles found matching your criteria"
        pagination={pagination}
        onPageChange={handlePageChange}
        onSearch={handleSearch}
        searchPlaceholder="Search cycles..."
        filterPanel={filterPanel}
        actions={tableActions}
        actionsType="dropdown"
        filters={
          hasActiveFilters ? (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {activeFilterCount} filter(s) active
              </span>
            </div>
          ) : null
        }
        onExport={handleExportCSV}
        exportLoading={isExporting}
      />

      {/* Edit Status Modal */}
      {selectedCycle && (
        <EditPrintingStatusModal
          selectedCycle={selectedCycle}
          openModal={isEditModalOpen}
          openChangeModal={setIsEditModalOpen}
        />
      )}

      {/* Vendor Assignment Modal */}
      {selectedCycle && (
        <AcceptBillCycleModal
          selectedCycle={selectedCycle}
          openModal={isVendorEditModalOpen}
          openChangeModal={setIsVendorEditModalOpen}
        />
      )}
      
      {selectedCycle && (
        <ViewPrintingDetailsModal
          selectedCycle={selectedCycle}
          openModal={isViewModalOpen}
          openChangeModal={setIsViewModalOpen}
        />
      )}
    </div>
  );
}
